import { useDesignSystem } from '@/composables/designSystem'
import { passesMenuGates, useActiveMenuElements } from '@/composables/menu'
import { useRoute } from '@/composables/route'
import { allowsMenuEntry } from '@/composables/subscription'
import { usePage } from '@inertiajs/vue3'
import { computed, ref } from 'vue'

/**
 * The organisation section uses the same page gates as the event section.
 */
const ORGANISATION_MENUS = [
  {
    name: 'OrganisationContent',
    label: 'content',
    dropdown: true,
    child: [
      {
        name: 'Dam/Index',
        label: 'media',
        labelKey: 'dam.nav.media',
        route: 'dam.web.index',
        feature: 'dam',
        permission: 'dam:view',
      },
      {
        name: 'Content/Index',
        label: 'texts',
        labelKey: 'content.texts.title',
        route: 'content.texts.index',
        feature: 'content',
        permission: 'content:text:list',
      },
    ],
  },
]

/**
 * The organisation's section menus with their visible pages.
 *
 * @returns {Array<object>}
 */
export const useOrganisationSectionMenus = () =>
  ORGANISATION_MENUS.map((menu) => ({
    ...menu,
    child: menu.child.filter(passesMenuGates),
  })).filter((menu) => menu.child.length > 0)

/**
 * The first page of the organisation's Inhalte section the current user may open, or null when
 * there is none: where its sidebar entry and breadcrumb lead.
 *
 * @returns {string|null}
 */
export const organisationContentHref = () => {
  const first = useOrganisationSectionMenus().find(
    (menu) => menu.name === 'OrganisationContent',
  )?.child[0]

  return first ? useRoute(first.route) : null
}

/**
 * The sidebar's sections that have a submenu, each with the children the current user may see.
 * Inside an event: the event sidebar's (menu.js `dropdown` entries) under the same gates as the
 * sidebar flyout (GroupedItems), i.e. useActiveMenuElements plus the subscription `limit`.
 * Outside one: the organisation level's (useOrganisationSectionMenus).
 *
 * @returns {Array<object>}
 */
export const useSectionMenus = () => {
  if (!usePage().props.event) {
    return useOrganisationSectionMenus()
  }

  return useActiveMenuElements()
    .filter((menu) => menu.dropdown === true)
    .map((menu) => ({
      ...menu,
      child: (menu.child ?? []).filter((child) => allowsMenuEntry(child)),
    }))
}

/**
 * Airstage pages that belong to the Sites sidebar entry. The host map and the breadcrumb trail
 * both read this list.
 */
export const AIRSTAGE_SITES_SUB_PAGES = ['Airstage/Forms', 'Airstage/Domains']

/**
 * Pages that belong to a section entry without being one themselves: a create or edit form, an
 * archive, an import, a log. Inertia reports their own component, which matches no entry in the
 * menu, so without this map the section is not found and the whole section column disappears the
 * moment you open one - you click "Create limit" and lose the navigation you came in through.
 *
 * Key: the page's component. Value: the listed section entry it belongs to.
 *
 * Guest-facing pages are deliberately absent (Emails/MarketingConsent/Confirm and OptOut are
 * public, event-less routes reached from an e-mail; they have no section and must not grow one).
 */
const SECTION_PAGE_HOSTS = {
  'Airstage/Builder': 'Airstage/Sites',
  'Airstage/Editor': 'Airstage/Sites',
  ...Object.fromEntries(
    AIRSTAGE_SITES_SUB_PAGES.map((component) => [component, 'Airstage/Sites']),
  ),
  'Airstage/ModuleBuilder': 'Airstage/Modules',
  'Bookings/Archived': 'Bookings/List',
  'Bookings/Checkins/Archived': 'Bookings/Checkins/List',
  'Bookings/Duplicates': 'Bookings/List',
  'Bookings/Import': 'Bookings/List',
  'Emails/Templates/Update': 'Emails/Templates/List',
  'EventApp/Navigation': 'EventApp/AppSettingsV2',
  'Events/Limits/Create': 'Events/Limits/List',
  'Events/Limits/Edit': 'Events/Limits/List',
  'Events/Reports/Show': 'Events/Reports/Index',
  'GuestGroups/QuotaApproval': 'GuestGroups/List',
  'SubEvents/Participations': 'SubEvents/List',
  'Workflows/Create': 'Workflows/List',
  'Workflows/Logs': 'Workflows/List',
  'Workflows/Update': 'Workflows/List',
}

/**
 * Sub-pages that serve more than one section entry, told apart by the `role` query parameter of
 * the current URL: the import imports guest managers from the guest manager list
 * (`?role=guest_manager`) and guests from the guest list.
 */
const ROLE_SECTION_PAGE_HOSTS = {
  'Bookings/Import': { guest_manager: 'Bookings/GuestManagers' },
}

const currentRole = () =>
  new URLSearchParams(usePage().url?.split('?')[1] ?? '').get('role')

/**
 * The section entry a page component belongs to - itself, unless it is one of the sub-pages above.
 *
 * @param {string} component
 * @returns {string}
 */
export const useSectionHostPage = (component) =>
  ROLE_SECTION_PAGE_HOSTS[component]?.[currentRole()] ??
  SECTION_PAGE_HOSTS[component] ??
  component

/**
 * The section the current page belongs to (its component, or the entry it is a sub-page of, is one
 * of the section's children), or null. A page listed in two sections (the device monitor) belongs
 * to the first.
 *
 * @returns {object|null}
 */
export const useCurrentSectionMenu = () => {
  const component = useSectionHostPage(usePage().component)

  return (
    useSectionMenus().find((menu) =>
      menu.child.some((child) => child.name === component),
    ) ?? null
  )
}

/**
 * The page of the current section whose visit is on its way: shown active at once (tabs and
 * section column) while the loading state covers the old page. Set and cleared by SectionTabs.
 */
export const pendingSectionPage = ref(null)

/**
 * Design-system look: pages shown as a tab of another page instead of an entry of their own
 * (page component => host page component). The host stays active while one of them is open.
 *
 * App Navigation belongs to App Config in both appearances through SECTION_PAGE_HOSTS.
 */
const DS_TAB_PAGES = {}

/**
 * Design-system look: section entries under another name (page component => common.* label).
 */
const DS_PAGE_LABELS = {
  'EventApp/AppSettingsV2': 'app_config',
}

/**
 * The current section's pages as links: the same entries and order as the sidebar flyout. The
 * event app's pages other than its setup are locked while the event has no event app (its
 * headline preview is null then; pages that do not send it leave every page usable).
 *
 * @returns {import('vue').ComputedRef<Array<object>>}
 */
export const useSectionPages = () =>
  computed(() => {
    const page = usePage()
    const section = useCurrentSectionMenu()

    if (section === null) {
      return []
    }

    const event = page.props.event
    const locked =
      section.name === 'EventApp' && page.props.eventAppTabsPreview === null
    const designSystem = useDesignSystem().value
    const hostOf = (component) =>
      designSystem ? (DS_TAB_PAGES[component] ?? component) : component

    return section.child
      .filter((child) => hostOf(child.name) === child.name)
      .map((child, index) => {
        const label = designSystem
          ? (DS_PAGE_LABELS[child.name] ?? child.label)
          : child.label

        return {
          name: child.name,
          label,
          labelKey: child.labelKey ?? `common.${label}`,
          infoTooltip: child.infoTooltip ?? null,
          icon: child.icon ?? null,
          group: child.group ?? null,
          href: event
            ? useRoute(child.route, { event: event.id, ...child.extraParams })
            : useRoute(child.route, child.extraParams),
          locked: locked && index > 0,
          premium:
            child.premium === true &&
            (page.props._eventAppPremium?.unbooked_modules ?? []).includes(
              child.feature,
            ),
          active:
            pendingSectionPage.value !== null
              ? hostOf(useSectionHostPage(pendingSectionPage.value)) ===
                child.name
              : hostOf(useSectionHostPage(page.component)) === child.name,
        }
      })
  })

const COLLAPSED_KEY = 'airlst.section-nav.collapsed'

const readCollapsed = () => {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Whether the section column (SectionNav) is folded away. The user folds it with its own button
 * and unfolds it by picking a section in the sidebar; the choice is remembered in this browser.
 */
export const sectionNavCollapsed = ref(readCollapsed())

/**
 * Whether the current page shows a page column that folds with the same choice as the section
 * column (the Airstage builder's pages). While it is folded, the sidebar shows the same expand
 * arrow as for a folded section column.
 */
export const foldablePageColumn = ref(false)

/**
 * @param {boolean} collapsed
 */
export const setSectionNavCollapsed = (collapsed) => {
  sectionNavCollapsed.value = collapsed
  try {
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0')
  } catch {
    // Storage blocked (private mode): the choice lasts for this page load only.
  }
}
